import React, { useState, useEffect, useCallback } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

function getAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (!sessionToken) return {};
  return { Authorization: `Bearer ${sessionToken}` };
}

function FeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState('');
  const [createName, setCreateName] = useState('');
  const [createEnabled, setCreateEnabled] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editFlag, setEditFlag] = useState(null);
  const [editName, setEditName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete
  const [deleteLoading, setDeleteLoading] = useState(null);

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/admin/feature-flags`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || 'Failed to load feature flags');
        return;
      }
      const list = data?.data?.flags ?? data?.flags ?? [];
      setFlags(Array.isArray(list) ? list : []);
    } catch (err) {
      setError('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const handleToggle = async (flag) => {
    setToggling(flag.key);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/feature-flags/${flag.key}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ is_enabled: !flag.is_enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || 'Failed to update flag');
        return;
      }
      setFlags((prev) =>
        prev.map((f) => (f.key === flag.key ? { ...f, is_enabled: !flag.is_enabled } : f))
      );
      setSuccess(`${flag.name || flag.key} ${!flag.is_enabled ? 'enabled' : 'disabled'}. Takes effect immediately.`);
    } catch (err) {
      setError('Failed to update flag');
    } finally {
      setToggling(null);
    }
  };

  // Create
  const handleCreate = async () => {
    setCreateError('');
    if (!createKey.trim()) { setCreateError('Key is required'); return; }
    if (!createName.trim()) { setCreateError('Name is required'); return; }
    setCreateLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/feature-flags`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ key: createKey.trim(), name: createName.trim(), is_enabled: createEnabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCreateError(data?.message || 'Failed to create flag');
        return;
      }
      setCreateOpen(false);
      setCreateKey('');
      setCreateName('');
      setCreateEnabled(true);
      setSuccess('Feature flag created.');
      fetchFlags();
    } catch (err) {
      setCreateError('Failed to create flag');
    } finally {
      setCreateLoading(false);
    }
  };

  // Edit
  const handleEditOpen = (flag) => {
    setEditFlag(flag);
    setEditName(flag.name || '');
    setEditError('');
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    setEditError('');
    if (!editName.trim()) { setEditError('Name is required'); return; }
    setEditLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/feature-flags/${editFlag.key}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ is_enabled: editFlag.is_enabled, name: editName.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEditError(data?.message || 'Failed to update flag');
        return;
      }
      setEditOpen(false);
      setEditFlag(null);
      setSuccess('Feature flag updated.');
      fetchFlags();
    } catch (err) {
      setEditError('Failed to update flag');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete
  const handleDelete = async (flag) => {
    if (!window.confirm(`Delete feature flag "${flag.name || flag.key}"? This cannot be undone.`)) return;
    setDeleteLoading(flag.key);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/feature-flags/${flag.key}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || 'Failed to delete flag');
        return;
      }
      setSuccess(`${flag.name || flag.key} deleted.`);
      fetchFlags();
    } catch (err) {
      setError('Failed to delete flag');
    } finally {
      setDeleteLoading(null);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12 d-flex align-items-center justify-content-between">
                <h3>Feature Flags</h3>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                >
                  Add Flag
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
              )}

              {loading ? (
                <Grid container justifyContent="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </Grid>
              ) : flags.length === 0 ? (
                <Typography color="text.secondary">No feature flags found.</Typography>
              ) : (
                flags.map((flag) => (
                  <Card key={flag.key} sx={{ mb: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <Typography variant="h6">{flag.name || flag.key}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Key: {flag.key}
                        </Typography>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!flag.is_enabled}
                              onChange={() => handleToggle(flag)}
                              disabled={toggling === flag.key}
                              color="primary"
                            />
                          }
                          label={
                            toggling === flag.key ? <CircularProgress size={18} /> :
                              flag.is_enabled ? 'Enabled' : 'Disabled'
                          }
                        />
                        <IconButton size="small" onClick={() => handleEditOpen(flag)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(flag)}
                          disabled={deleteLoading === flag.key}
                        >
                          {deleteLoading === flag.key ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Feature Flag</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setCreateError('')}>{createError}</Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Key" value={createKey}
                onChange={(e) => setCreateKey(e.target.value)}
                required variant="outlined"
                helperText="Unique identifier (e.g. ai-chat, dark-mode)"
                placeholder="my-feature"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Name" value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                required variant="outlined"
                helperText="Display name"
                placeholder="My Feature"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch checked={createEnabled} onChange={(e) => setCreateEnabled(e.target.checked)} color="primary" />
                }
                label={createEnabled ? 'Enabled' : 'Disabled'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={18} color="inherit" /> : null}>
            {createLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Feature Flag</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setEditError('')}>{editError}</Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Key" value={editFlag?.key || ''} disabled variant="outlined" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Name" value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={18} color="inherit" /> : null}>
            {editLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Component>
  );
}

export default FeatureFlags;
